import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, AlarmClock, TriangleAlert } from "lucide-react";
import { NAPS } from "@/lib/sleepData";

export default function NapTimer() {
  const [pick, setPick] = useState(NAPS[0]);
  const [left, setLeft] = useState(NAPS[0].min * 60);
  const [on, setOn] = useState(false);
  const total = pick.min * 60;
  const ended = useRef(false);

  useEffect(() => { setLeft(pick.min * 60); setOn(false); ended.current = false; }, [pick]);
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => setLeft((l) => (l <= 1 ? (setOn(false), 0) : l - 1)), 1000);
    return () => clearInterval(t);
  }, [on]);

  const pct = 1 - left / total;
  const circ = 2 * Math.PI * 86;

  return (
    <section className="relative overflow-hidden" style={{ background: "#0E1230" }} data-testid="sleep-nap">
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-semibold" style={{ background: "rgba(143,208,232,0.14)", color: "#8FD0E8" }}>
            <AlarmClock size={11} /> the difference between twenty and ninety
          </span>
          <h2 className="font-display text-[32px] sm:text-[44px] leading-[1.02] mt-4 text-white">Three naps, and only two of them help</h2>
          <div className="mt-7 space-y-2">
            {NAPS.map((n) => (
              <button key={n.min} onClick={() => setPick(n)} className="w-full text-left rounded-2xl px-5 py-4 transition-all hover:translate-x-1"
                style={pick.min === n.min ? { background: "rgba(255,255,255,0.09)", border: `1px solid ${n.tone}66` } : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}
                data-testid={`nap-${n.min}`}>
                <div className="flex items-center gap-3">
                  <p className="font-display text-2xl tabular-nums shrink-0" style={{ color: n.tone }}>{n.min}</p>
                  <p className="font-display text-[17px] text-white flex-1">{n.name}</p>
                  {n.warn && <TriangleAlert size={14} style={{ color: n.tone }} />}
                </div>
              </button>
            ))}
          </div>
          <AnimatePresence mode="wait">
            <motion.p key={pick.min} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="text-[14px] mt-5 max-w-md text-white/55" data-testid="nap-say">{pick.say}</motion.p>
          </AnimatePresence>
        </div>

        <div className="lg:col-span-7 flex justify-center">
          <button onClick={() => setOn(!on)} className="relative w-[240px] h-[240px] sm:w-[280px] sm:h-[280px] flex items-center justify-center" data-testid="nap-toggle">
            <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full -rotate-90">
              <circle cx="100" cy="100" r="86" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
              <motion.circle cx="100" cy="100" r="86" fill="none" stroke={pick.tone} strokeWidth="8" strokeLinecap="round"
                strokeDasharray={circ} animate={{ strokeDashoffset: circ * (1 - pct) }} transition={{ duration: 0.6 }} />
            </svg>
            <motion.span className="absolute rounded-full" style={{ inset: 46, background: `radial-gradient(circle at 38% 32%, ${pick.tone}44, transparent 70%)` }}
              animate={on ? { scale: [1, 1.06, 1], opacity: [0.7, 1, 0.7] } : { scale: 1, opacity: 0.5 }} transition={{ duration: 5, repeat: Infinity }} />
            <div className="relative text-center">
              <p className="font-display text-[46px] leading-none tabular-nums text-white">
                {String(Math.floor(left / 60)).padStart(2, "0")}:{String(left % 60).padStart(2, "0")}
              </p>
              <p className="text-[12px] mt-3 inline-flex items-center gap-2 text-white/45">
                {on ? <><Pause size={12} /> lie down, it is running</> : <><Play size={12} /> tap to start</>}
              </p>
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}
